import { Progress, TableColumn, Table } from "@backstage/core-components";
import { Spacer } from "@internal/plugin-utilities";
import { Chip, Typography } from "@material-ui/core";
import React from "react";
import { VIOLATION_TABLE } from "@internal/plugin-ops-review-common";
import { TeamBreakdownHeader } from "../../TeamBreakdownHeader";

export interface Props {
  data: any;
  loading: boolean;
}

export const HoneybadgerFaultsSection: React.FC<Props> = ({
  data,
  loading,
}): JSX.Element => {
  if (loading) {
    return <Progress />;
  }

  const columns: TableColumn<any>[] = [
    {
      title: "Project ID",
      field: "projectId",
      render: (rowData) => {
        const url = `https://app.honeybadger.io/projects/${rowData.project_id}/faults`;
        return (
          <Typography>
            <a href={url} target="_blank">
              {rowData.project_id}
            </a>
          </Typography>
        );
      },
      width: "10%",
    },
    {
      title: "Count",
      field: "count",
      render: (rowData) => rowData.notices_count_in_range,
      width: "10%",
    },
    {
      title: "Name",
      field: "name",
      render: (rowData) => (
        <a href={rowData.url} target="_blank">
          <Typography>
            <strong>{rowData.klass}</strong>
          </Typography>
        </a>
      ),
    },
    {
      title: "jira",
      field: "Jira",
      render: (rowData) => {
        const tags = rowData.tags;
        let associated;
        tags.forEach((tag: string) => {
          if (tag.indexOf("jira:") !== 0) {
            return;
          }
          const ticket = tag.split(":")[1];
          const url = `https://kajabi.atlassian.net/browse/${ticket}`;
          associated = (
            <Typography>
              <a href={url} target="_blank">
                {ticket}
              </a>
            </Typography>
          );
        });
        return associated;
      },
    },
    {
      title: "component",
      field: "Component",
      render: (rowData) => <Typography>{rowData.component}</Typography>,
    },
    {
      title: "action",
      field: "Action",
      render: (rowData) => <Typography>#{rowData.action}</Typography>,
    },
    {
      title: "Tags",
      field: "tags",
      render: (rowData) =>
        rowData.tags.map((tag: string) => (
          <Chip key={tag} label={tag} size="small" />
        )),
    },
  ];

  const sortByKey = (array: any[], key: string) => {
    return array.sort((a, b) => {
      return b[key] - a[key];
    });
  };

  const tableData = sortByKey(data.data, "notices_count_in_range");

  const jsx = (
    <Table
      options={{
        sorting: false,
        search: false,
        paging: false,
        pageSize: 20,
        padding: "dense",
      }}
      columns={columns}
      data={tableData}
    />
  );

  const tooltip = (
    <>
      <Typography variant="h5">
        {VIOLATION_TABLE.honeybadger.faults_above_threshold.display}
      </Typography>
      <div>
        This section displays any Honeybadger faults that exceed a count of{" "}
        <code>50</code> within a 7 day period. This threshold is adjustable if
        when necessary.
        <br />
        You can associate a Jira ticket with a fault by adding a
        `jira:YOUR_TICKET_NUMBER` tag to the fault in Honeybadger.
      </div>
    </>
  );

  return (
    <div>
      <TeamBreakdownHeader
        title={VIOLATION_TABLE.honeybadger.faults_above_threshold.display}
        vendor="honeybadger"
        tooltip={tooltip}
      />
      <Spacer variant="normal" />
      {jsx}
    </div>
  );
};
